import { Component, ReactNode, Suspense, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Router } from '@uhg-abyss/web/ui/Router';
import { AppHeader } from '@src/apps/AppHeader/AppHeader';
import { CommonAppHeader } from '@src/apps/Header/Main/CommonAppHeader';
import { HamMenu } from '@src/apps/HamMenu/HamMenu';
import { Footer } from '@src/apps/Footer/Footer';
import { environment } from '@web/config';
import { useAuth } from '@web/auth';
import { useIdleTimeout, IdleTimeoutDialog } from '@web/common/idle-timeout';
import { useShellEvents, ProviderSearchModal } from '@web/common/shell-events';
import { Faq } from '@src/apps/Faq/Faq';
import ContractIdEditorHost from '@src/CommonComponents/ContractIdFilter/ContractIdEditorHost';
import styles from './AppShell.module.css';

type AppShellStyles = {
  uhciAppShell: string;
  headerNav: string;
  mainContent: string;
  mainContentNoTopPadding: string;
};

const appShellStyles = styles as unknown as AppShellStyles;

type UhciWindow = Window & {
  __uhciHistoryEventsPatched?: boolean;
};

const JOURNEY_ID_BY_PATH: Record<string, string> = {
  'overview-page-advocate': '2',
  myhome: '1',
  reimbursements: '3,301',
  'reimbursements/non-payments': '3,303',
  'reimbursements/payments': '3,302',
  'reimbursements/appeals': '3,306',
  'reimbursements/reconsiderations': '3,304',
  'reimbursements/overpayments': '3,305',
  'network-management/adm-report': '4,402',
  'network-management/affordability-summary': '4,401',
  'network-management/vbc': '4,406',
  'network-management/exemption-summary': '4,405',
  'prior-auth': '5,501',
  'prior-auth/gold-card': '5,501',
  'patient-care-opportunity': '6',
  'service-interaction/calls': '7,704',
  'service-interaction/selfservice': '71,701',
  'service-interaction/edi': '71,701',
  'service-interaction/smart-edits': '71,703',
  'service-interaction/precheck-myscript': '71,704',
  'ancillary/labs': '8,803',
  'ancillary/imaging': '8,805',
  scorecard: '10,1001',
  'scorecard/heatmap': '10,1002',
};

const getJourneyIdForPath = (pathname: string) => {
  const normalizedPath = pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  return JOURNEY_ID_BY_PATH[normalizedPath] || '';
};

/** Catches parcel crashes so one broken parcel doesn't take down the whole shell */
class ParcelErrorBoundary extends Component<
  { name: string; children: ReactNode },
  { error: string | null }
> {
  state = { error: null };
  static getDerivedStateFromError(e: Error) {
    return { error: e.message };
  }
  componentDidCatch(e: Error) {
    console.error(`[AppShell] ${this.props.name} crashed:`, e);
  }
  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: '8px 16px',
            background: '#fff3cd',
            color: '#856404',
            fontSize: '0.8rem',
          }}
        >
          ⚠ {this.props.name} failed to load: {this.state.error}
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * AppShell — mirrors the Angular app structure:
 *
 *   <app-root>
 *     <main>
 *       <app-body>
 *         <app-hamburger-menu>   ← full-width top nav bar (NOT a sidebar)
 *           <router-outlet>      ← routed page content rendered INSIDE NavigationBar
 *         </app-hamburger-menu>
 *       </app-body>
 *     </main>
 *   </app-root>
 *
 * Layout (all stacked vertically, full-width):
 *   ┌──────────────────────────────┐
 *   │         AppHeader            │  top branding/account bar
 *   ├──────────────────────────────┤
 *   │     HamMenu (top nav bar)    │  primary + secondary nav (horizontal)
 *   ├──────────────────────────────┤
 *   │      <Outlet /> (page)       │  routed page content, full width
 *   ├──────────────────────────────┤
 *   │          Footer              │
 *   └──────────────────────────────┘
 */
export const AppShell = () => {
  const navigate = useNavigate();
  const { authenticated, logout } = useAuth();
  const { pathname } = useLocation();
  const { providerDialogOpen, closeProviderDialog } = useShellEvents();
  const journeyId = getJourneyIdForPath(pathname);

  const sessionReady = !!sessionStorage.getItem('currentUser');

  // Idle timeout — mirrors Angular's angular-user-idle behavior
  const handleIdleTimeout = useCallback(() => {
    logout('idle');
  }, [logout]);

  const { showWarning, secondsLeft, stayLoggedIn } = useIdleTimeout(
    {
      idleSeconds: environment.idleTimeOut ?? 1620,
      timeoutSeconds: environment.idleWarningTimeOut ?? 180,
      enabled: authenticated,
    },
    handleIdleTimeout
  );

  // Keep legacy journey consumers in sync without storing RouteConfig-derived data.
  useEffect(() => {
    sessionStorage.setItem('journeyId', journeyId);
    window.dispatchEvent(new CustomEvent('uhci-journeychange', { detail: journeyId }));
    window.dispatchEvent(new Event('uhci-routechange'));
  }, [journeyId]);

  useEffect(() => {
    const getCurrentUrl = () =>
      `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const onReactNavigation = (event: Event) => {
      const url = (event as CustomEvent<{ url?: string }>).detail?.url;

      if (!url || url === '#') {
        return;
      }

      const nextUrl = new URL(url, window.location.origin);

      if (nextUrl.origin !== window.location.origin) {
        window.location.assign(nextUrl.href);
        return;
      }

      const nextRoute = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

      if (nextRoute === getCurrentUrl()) {
        return;
      }

      navigate(nextRoute);
    };

    // Router navigation uses history API; dispatch custom events so shell reacts to all route changes.
    const uhciWindow = window as UhciWindow;
    if (!uhciWindow.__uhciHistoryEventsPatched) {
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      window.history.pushState = function (...args: Parameters<History['pushState']>) {
        const result = originalPushState.apply(this, args);
        window.dispatchEvent(new Event('uhci-routechange'));
        return result;
      };

      window.history.replaceState = function (...args: Parameters<History['replaceState']>) {
        const result = originalReplaceState.apply(this, args);
        window.dispatchEvent(new Event('uhci-routechange'));
        return result;
      };

      uhciWindow.__uhciHistoryEventsPatched = true;
    }

    window.addEventListener('reactNavigation', onReactNavigation);
    return () => {
      window.removeEventListener('reactNavigation', onReactNavigation);
    };
  }, [navigate]);

  const normalizedPath = pathname.toLowerCase();
  const isExternalUser = !environment.internalAccess;
  const isAccessDeniedPage =
    normalizedPath === '/accessdenied' || normalizedPath.startsWith('/accessdenied/');
  const shouldShowNavigationShell = !(isExternalUser && isAccessDeniedPage);

  const isAiGenieHomePage = /(^|\/)pype-home(\/|$)/.test(normalizedPath);
  // Mirror Angular: boolRemoveLeftNav=true for reactExdashboardsRoutes (external iframe apps hide the nav)
  const isExternalDashboardPage =
    normalizedPath.includes('/segmentationbuilder') ||
    normalizedPath.includes('/mysavedreports') ||
    normalizedPath.includes('/providerportaldashboard') ||
    normalizedPath.includes('/claimsapiprovider') ||
    normalizedPath.includes('/userlookuptool');
  const shouldShowHamMenu =
    !normalizedPath.includes('/myhome') &&
    !normalizedPath.includes('/content-editor') &&
    !isAiGenieHomePage &&
    !isExternalDashboardPage;

  const isPsmsPage = normalizedPath.includes('/psms');
  const isHeatmapPage = normalizedPath.includes('/heatmap');

  return (
    <div className={appShellStyles.uhciAppShell}>
      {shouldShowNavigationShell && (
        <div className={appShellStyles.headerNav}>
          {/* Top branding/account bar — defer until session is confirmed ready */}
          {sessionReady && (
            <ParcelErrorBoundary name={isPsmsPage ? 'Header' : 'AppHeader'}>
              <Suspense fallback={null}>
                {isPsmsPage ? (
                  <AppHeader notificationContextKey={pathname} />
                ) : (
                  <CommonAppHeader notificationContextKey={pathname} />
                )}
              </Suspense>
            </ParcelErrorBoundary>
          )}

          {/* Full-width horizontal navigation bar — sticky below header */}
          {shouldShowHamMenu && (
            <ParcelErrorBoundary name="HamMenu">
              <Suspense fallback={null}>
                <HamMenu />
              </Suspense>
            </ParcelErrorBoundary>
          )}
        </div>
      )}

      {/* Routed page content — flows below the sticky header */}
      <main
        className={`${appShellStyles.mainContent} ${isHeatmapPage ? appShellStyles.mainContentNoTopPadding : ''
          }`.trim()}
      >
        <Router.Outlet />
      </main>

      <ContractIdEditorHost />

      {/* Footer */}
      <ParcelErrorBoundary name="Footer">
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </ParcelErrorBoundary>

      {/* Provider Search Dialog — opened by openProviderDialog shell event */}
      {providerDialogOpen && <ProviderSearchModal onClose={closeProviderDialog} />}

      {/* FAQ button (fixed, right-side) + drawer — mirrors Angular app.component.html app-faq-button + app-faq-drawer */}
      <ParcelErrorBoundary name="Faq">
        <Faq />
      </ParcelErrorBoundary>

      {/* Idle timeout warning dialog */}
      {showWarning && (
        <IdleTimeoutDialog
          secondsLeft={secondsLeft}
          onStayLoggedIn={stayLoggedIn}
          onLogout={handleIdleTimeout}
        />
      )}
    </div>
  );
};
